"use client";

import React, { useState } from "react";
import { ResponsiveModal, ModalContainer, MODAL_IDS } from "@wtc/ui";
import { AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import type { WithdrawContractModalData } from "@/lib/modals/data/modal-data-types";

// Mounted globally via DetailPageModals. Destructive confirm modal: receives the
// Hub contract id + label through the modal-stack data payload and POSTs to
// /api/contracts/[id]/withdraw (admin-only) itself. It does NOT read
// DetailActionContext — that is null at the global mount. After a successful
// withdraw it calls the data payload's onConfirmed callback so the source view
// can refresh. See GLOBAL_MODAL_NO_ACTION_CONTEXT + ACTION_MODAL_MOUNTS.

export function WithdrawContractModal() {
  return (
    <ModalContainer<WithdrawContractModalData> modalId={MODAL_IDS.contracts.withdraw}>
      {({ isOpen, data, onClose }) => (
        <WithdrawContractModalContent isOpen={isOpen} data={data} onClose={onClose} />
      )}
    </ModalContainer>
  );
}

interface WithdrawContractModalContentProps {
  isOpen: boolean;
  data?: WithdrawContractModalData & { onConfirmed?: () => void | Promise<void> };
  onClose: () => void;
}

function WithdrawContractModalContent({
  isOpen,
  data,
  onClose,
}: WithdrawContractModalContentProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const contractId = data?.contractId;
  if (!contractId) return null;

  const label = data?.contractLabel?.trim() || "this contract";

  const handleWithdraw = async () => {
    setIsSubmitting(true);
    try {
      const response = await fetch(`/api/contracts/${encodeURIComponent(contractId)}/withdraw`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      const payload: { success?: boolean; error?: string } = await response
        .json()
        .catch(() => ({}));

      if (!response.ok || !payload.success) {
        toast.error(payload.error || "Couldn't withdraw the contract.");
        return;
      }

      toast.success("Contract withdrawn. To re-send, generate and send it again.");
      await data?.onConfirmed?.();
      onClose();
    } catch {
      toast.error("Couldn't withdraw the contract. Try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ResponsiveModal
      globalModalId={MODAL_IDS.contracts.withdraw}
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title="Withdraw / void contract"
      description={`Withdraw ${label}? This voids the signature request for everyone. There is no undo — to start over you'll generate and send a new contract.`}
      maxWidth="md"
    >
      <div className="p-4 space-y-4">
        <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3 dark:border-amber-800 dark:bg-amber-950/40">
          <AlertTriangle className="h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400" />
          <p className="text-sm text-amber-800 dark:text-amber-200">
            Withdrawing cancels the eSignatures request and marks the contract as
            withdrawn. Any signing links stop working immediately. This cannot be
            reversed — the only way back is to re-send a fresh contract.
          </p>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={handleWithdraw}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Withdrawing…" : "Withdraw contract"}
          </Button>
        </div>
      </div>
    </ResponsiveModal>
  );
}
